/* eslint-disable react/function-component-definition */
/* eslint-disable import/prefer-default-export */
import React from 'react';
import { Area, Bar, Line } from 'recharts';
import moment from 'moment-timezone';

import { graphMetrics } from './Metrics';
import { getMetricDataForDateRange, getMetricDataForDateRange_End, getMetricDataForDateRange_Start } from './DataHelpers';
import { getMetricDefaultFormat } from './Formatting';
import { drawDotShape } from './Components';
import { BAD_METRIC_DOT_COLOR, graphValidTakeDotColor } from './Constants';
import { manipulateColor } from './ColorTools';
import { normalizePair, prepareSimplifiedDataForChart } from './simplifyData';

const CustomizedDot = props => {
  const { value, badData, goodData, format, cursor, name, payload } = props;
  const bad = badData && badData(value);
  const good = goodData && goodData(value, payload?.x ? moment.unix(payload.x) : null);
  const shape = format.dotShape;

  const stroke = bad ? BAD_METRIC_DOT_COLOR : good ? graphValidTakeDotColor : format.stroke;
  const fill = bad ? BAD_METRIC_DOT_COLOR : manipulateColor(0.4, stroke);

  const dataInfo = `${name}-${payload?.x}-${value}`;

  // console.log(props);
  // console.log(bad, shape);

  return drawDotShape(
    shape,
    {
      ...props,
      r: props.r * 4,
      strokeDasharray: '',
      fill,
      stroke,
    },
    dataInfo,
    cursor,
  );
};

export function drawLineSeries(selectedMetrics, yAxisGroups, data, dateRange, maxRange, pointerCursor = false, simplifiedVariant = false, options = {}) {
  const { showAlertThresholds, showGoals, showTrendlines, metricForThresholds } = options;
  const getMetricData = (data, dateRange, metricExt) => {
    const metricData = getMetricDataForDateRange(data, dateRange, metricExt.id);
    if (!metricExt.dontSimplify) {
      metricData.data = prepareSimplifiedDataForChart(metricData.data);
    }
    return metricData;
  };
  const getSeriesData = metricData => {
    return metricData.data.map(i => {
      return { x: i.dt.unix(), y: i.value, statistics: i.statistics, distribution: i.distribution };
    });
  };

  let index = 0;
  const selectedMetricsExt = selectedMetrics.map(metric => graphMetrics.find(gm => gm.id === metric));

  const ret = [];
  selectedMetricsExt.forEach(metricExt => {
    if (!data[metricExt.id] || !data[metricExt.id].data || data[metricExt.id].data.length === 0) return null;

    const metricData = getMetricData(data, dateRange, metricExt);
    const seriesData = getSeriesData(metricData);

    const format = getMetricDefaultFormat(index);
    const metricFormat = metricExt.format || {};
    Object.assign(format, metricFormat);

    index++;
    // console.log(`series ${metricExt.id}: ${seriesData.length} elements`);

    const yAxisGroup = yAxisGroups.find(yAG => yAG.metricsExt.some(m => m.id === metricExt.id));
    const yAxisId = yAxisGroup.axisId;
    const yAxisIdOK = yAxisGroup.axisId === 'yAxis2' || yAxisGroup.axisId === 'yAxis1';
    const yAxisIsRight = yAxisId === 'yAxis2';

    if (!yAxisIdOK) return null;

    const isBar = metricExt.format.chartType === 'bar';
    const isDistribution = metricExt.format.chartType === 'distribution';
    const isCategoryDistribution = metricExt.format.chartType === 'categoryDistribution';

    // console.log('metricData', metricData);
    // console.log('seriesData', seriesData);

    const SimpleBar = props => {
      const { yAxis, payload, background, color } = props;

      // console.log('SimpleBar', props);

      if (payload.statistics) {
        const yStart = background.y;
        const yEnd = background.y + background.height;
        const yAxisStart = yAxis.yMinReal;
        const yAxisEnd = yAxis.yMaxReal;

        const yRange = normalizePair(
          yAxisStart,
          yAxisEnd,
          payload.statistics.minValue,
          payload.statistics.maxValue,
          yStart,
          yEnd,
        );
        if (yRange.v1 === yRange.v2) {
          yRange.v1--;
          yRange.v2++;
        }

        const width = 7;

        return (
          <rect
            x={background.x + 1 - width / 2}
            y={background.height - yRange.v2}
            width={width}
            height={yRange.v2 - yRange.v1}
            fill={color}
          />
        );
      }

      if (payload.y) {
        const yStart = background.y;
        const yEnd = background.y + background.height;
        const yAxisStart = yAxis.yMinReal;
        const yAxisEnd = yAxis.yMaxReal;

        const yRange = normalizePair(yAxisStart, yAxisEnd, 0, payload.y, yStart, yEnd);
        if (yRange.v1 === yRange.v2) {
          yRange.v1--;
          yRange.v2++;
        }

        const width = 7;

        return (
          <rect
            x={background.x + 1 - width / 2}
            y={background.height - yRange.v2}
            width={width}
            height={yRange.v2 - yRange.v1}
            fill={color}
          />
        );
      }

      return null;
    };

    const DistributionBar = props => {
      const { yAxis, payload, background, color, resolution } = props;

      // console.log('DistributionBar', props);

      if (payload.statistics) {
        const yStart = background.y;
        const yEnd = background.y + background.height;
        const yAxisStart = yAxis.yMinReal;
        const yAxisEnd = yAxis.yMaxReal;

        const ret = (
          <g>
            {payload.statistics.values.map((i, index) => {
              const opacity = i.count >= 10 ? '0%' : `${i.count * 10}%`;
              const manipulateRatio = i.count >= 10 ? 0 : 1 - i.count / 10;
              const fillColor = manipulateColor(manipulateRatio, color, '#fff');
              const strokeColor = manipulateColor(manipulateRatio * 0.7, color, '#fff');

              const yRange = normalizePair(
                yAxisStart,
                yAxisEnd,
                i.bucket * resolution,
                i.bucket * resolution + resolution,
                yStart,
                yEnd,
              );
              const width = 7;

              // console.log(i, yAxisStart, yAxisEnd, i.value, i.value + resolution, yStart, yEnd);
              // console.log('yRange', yRange);

              return (
                <rect
                  key={`Distribution_${metricExt.id}_part_${index}`}
                  style={{ strokeWidth: 1, stroke: `${strokeColor}` }}
                  x={background.x + 1 - width / 2}
                  y={background.height - yRange.v2}
                  width={width}
                  height={yRange.v2 - yRange.v1}
                  fill={fillColor}
                  //opacity={opacity}
                  rx={5}
                  ry={2}
                />
              );
            })}
          </g>
        );

        return ret;
      }

      return null;
    };

    const CategoryDistributionBar = props => {
      const { yAxis, payload, background, colors } = props;

      // console.log('CategoryDistributionBar', props);

      if (payload.distribution) {
        const yStart = background.y;
        const yEnd = background.y + background.height;
        const yAxisStart = yAxis.yMinReal;
        const yAxisEnd = yAxis.yMaxReal;

        const keys = Object.keys(payload.distribution);

        let sum = 0;
        const ret = (
          <g>
            {keys.map((key, index) => {
              const value = payload.distribution[key];
              const fillColor = colors[key];
              const strokeColor = colors[key];

              const yRange = normalizePair(yAxisStart, yAxisEnd, sum, sum + value, yStart, yEnd);
              const width = 7;

              // console.log(key, yAxisStart, yAxisEnd, sum, sum + value, yStart, yEnd);
              // console.log('yRange', yRange);

              sum += value;

              return (
                <rect
                  key={`Distribution_${metricExt.id}_part_${index}`}
                  style={{ strokeWidth: 1, stroke: `${strokeColor}` }}
                  x={background.x + 1 - width / 2}
                  y={background.height - yRange.v2}
                  width={width}
                  height={yRange.v2 - yRange.v1}
                  fill={fillColor}
                  //opacity={opacity}
                  rx={5}
                  ry={2}
                />
              );
            })}
          </g>
        );

        return ret;
      }

      return null;
    };

    const drawThresholds = showAlertThresholds && metricForThresholds === metricExt.id && !simplifiedVariant;
    if (data[metricExt.id].thresholdMin && drawThresholds && (metricData.inRangeCount > 0 || (metricData.hasPrev && metricData.hasNext))
    ) {
      ret.push(
        <Area
          type="monotone"
          yAxisId="yAxis1"
          connectNulls
          activeDot={false}
          dot={false}
          stroke={BAD_METRIC_DOT_COLOR}
          strokeWidth={1}
          strokeOpacity={0.4}
          fillOpacity={0.1}
          fill={BAD_METRIC_DOT_COLOR}
          data={[
            { x: maxRange.start?.clone().add(-1, 'day').unix(), y: [yAxisGroup.yMinReal, data[metricExt.id].thresholdMin] },
            { x: maxRange?.end?.clone().add(1, 'day').unix(), y: [yAxisGroup.yMinReal, data[metricExt.id].thresholdMin] }
          ]}
          dataKey="y"
          key={`ThresholdMinArea_${metricExt.id}_${index}`}
          tooltipType="none"
        />
      );
    };
    if (data[metricExt.id].thresholdMax &&
      drawThresholds &&
      (metricData.inRangeCount > 0 || (metricData.hasPrev && metricData.hasNext))
    ) {
      ret.push(
        <Area
          type="monotone"
          yAxisId="yAxis1"
          connectNulls
          activeDot={false}
          dot={false}
          stroke={BAD_METRIC_DOT_COLOR}
          strokeWidth={1}
          strokeOpacity={0.4}
          fillOpacity={0.1}
          fill={BAD_METRIC_DOT_COLOR}
          data={[
            { x: maxRange.start?.clone().add(-1, 'day').unix(), y: [data[metricExt.id].thresholdMax, yAxisGroup.yMaxReal] },
            { x: maxRange?.end?.clone().add(1, 'day').unix(), y: [data[metricExt.id].thresholdMax, yAxisGroup.yMaxReal] }
          ]}
          dataKey="y"
          key={`ThresholdMaxArea_${metricExt.id}_${index}`}
          tooltipType="none"
        />
      );
    };

    const drawGoals = showGoals && metricForThresholds === metricExt.id && !simplifiedVariant;
    if (data[metricExt.id].goals && drawGoals && (metricData.inRangeCount > 0 || (metricData.hasPrev && metricData.hasNext))) {
      const goalData = [];
      data[metricExt.id].goals.forEach(g => {
        goalData.push({ x: g.from.unix(), y: [g.min || yAxisGroup.yMinReal, g.max || yAxisGroup.yMaxReal] });
        goalData.push({ x: g.to.unix(), y: [g.min || yAxisGroup.yMinReal, g.max || yAxisGroup.yMaxReal] });
      });
      ret.push(
        <Area
          type="monotone"
          yAxisId="yAxis1"
          connectNulls
          activeDot={false}
          dot={false}
          stroke={graphValidTakeDotColor}
          strokeWidth={1}
          strokeOpacity={0.4}
          fillOpacity={0.1}
          fill={graphValidTakeDotColor}
          data={goalData}
          dataKey="y"
          key={`GoalRefArea_${metricExt.id}_${index}`}
          tooltipType="none"
        />
      );
    }
    
    if (!isBar && !isDistribution && !isCategoryDistribution) {
      ret.push(
        <Line
          key={`Line_${metricExt.id}`}
          type={format.type}
          tooltipType="none"
          name={metricExt.label + (yAxisIsRight ? ' (R)' : '')}
          stroke={manipulateColor(0.5, format.stroke, '#fff')}
          strokeWidth={format.strokeWidth}
          data={seriesData}
          dataKey="y"
          connectNulls
          dot={simplifiedVariant ? false : <CustomizedDot format={format} badData={data[metricExt.id].badData} goodData={data[metricExt.id].goodData} cursor={pointerCursor} />}
          activeDot={false}
          strokeDasharray={format.strokeDasharray} // {!yAxisIsRight ? format.strokeDasharray : '2 2'}
          isAnimationActive={false}
          yAxisId={yAxisId}
        />
      );

      const withTrendLine = showTrendlines;
      const strokeDasharrayForTrendLine = '1 2';

      if(!simplifiedVariant && withTrendLine && seriesData.length > 2) {
        const calculateTrendLine = dataForTrend => {
          const n = dataForTrend.length;
          if (n < 2) return [];

          const sumX = dataForTrend.reduce((sum, point) => sum + point.x, 0);
          const sumY = dataForTrend.reduce((sum, point) => sum + point.y, 0);
          const sumXY = dataForTrend.reduce((sum, point) => sum + point.x * point.y, 0);
          const sumX2 = dataForTrend.reduce((sum, point) => sum + point.x * point.x, 0);

          const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
          const intercept = (sumY - slope * sumX) / n;

          return { 
            data: [
              { x: dataForTrend[0].x, y: slope * dataForTrend[0].x + intercept },
              { x: dataForTrend[n - 1].x, y: slope * dataForTrend[n - 1].x + intercept },
            ],
            slope,
            intercept
          };
        };

        // calc trend line values for the first and last data point in range
        const dataForTrend = seriesData.map(point => ({ x: point.x, y: +point.y }));
        const trendLine = calculateTrendLine(dataForTrend);
     
        // normalize trend line values to fit the chart x axis range
        const dtStart = getMetricDataForDateRange_Start(dateRange);
        const dtEnd = getMetricDataForDateRange_End(dateRange);
        const trendLineNormalized = [{x: dtStart, y: dtStart * trendLine.slope + trendLine.intercept}, {x: dtEnd, y: dtEnd * trendLine.slope + trendLine.intercept}];

        // check if trend line values are within y axis extended range (extended by the same range as the current y axis range)
        //  - if not, don't draw the trend line, as it would be misleading
        const theYAxis = yAxisGroups.find(yAG => yAG.axisId === yAxisId);
        const yAxisHeight = theYAxis.yMaxReal - theYAxis.yMinReal;
        const yAxisExtended = {min: theYAxis.yMinReal - yAxisHeight, max: theYAxis.yMaxReal + yAxisHeight};
        const isTL1OK = trendLineNormalized[0].y >= yAxisExtended.min && trendLineNormalized[0].y <= yAxisExtended.max;
        const isTL2OK = trendLineNormalized[1].y >= yAxisExtended.min && trendLineNormalized[1].y <= yAxisExtended.max;
        // console.log('Trend line for ', metricExt.id, ', slope:', trendLine.slope, trendLine.data, trendLineNormalized, yAxisExtended);

        if(!isTL1OK || !isTL2OK) {
          console.log('Trend line for ', metricExt.id, ' is out of yAxis bounds, not drawing it.', trendLineNormalized, yAxisExtended);
        }
        else {
          ret.push(
            <Line
              key={`Line_${metricExt.id}_TrendLine`}
              type={format.type}
              tooltipType="none"
              name={`${metricExt.label} trend ${yAxisIsRight ? ' (R)' : ''}`}
              stroke={manipulateColor(0.5, format.stroke, '#fff')}
              strokeWidth={format.strokeWidth}
              data={trendLineNormalized}
              dataKey="y"
              connectNulls
              dot={false}
              activeDot={false}
              strokeDasharray={strokeDasharrayForTrendLine}
              isAnimationActive={false}
              yAxisId={yAxisId}
            />
          );
        }
      }
    }

    if (isBar) {
      ret.push(
        <Bar
          key={`Bar_${metricExt.id}`}
          name={metricExt.label + (yAxisIsRight ? ' (R)' : '')}
          fill={manipulateColor(0.5, format.stroke, '#fff')}
          data={seriesData}
          dataKey="y"
          isAnimationActive={false}
          yAxisId={yAxisId}
          shape={<SimpleBar yAxis={yAxisGroup} color={metricExt.format.color} />}
          barSize={0}
        />
      );
    }
    if (isDistribution) {
      ret.push(
        <Bar
          key={`Distribution_${metricExt.id}`}
          name={metricExt.label + (yAxisIsRight ? ' (R)' : '')}
          fill={manipulateColor(0.5, format.stroke, '#fff')}
          data={seriesData}
          dataKey="y"
          isAnimationActive={false}
          yAxisId={yAxisId}
          shape={(
            <DistributionBar
              yAxis={yAxisGroup}
              color={metricExt.format.color}
              resolution={metricExt.format.resolution}
            />
          )}
          barSize={0}
        />
      );
    }
    if (isCategoryDistribution) {
      ret.push(
        <Bar
          key={`Distribution_stackId_${metricExt.format.stackId}`}
          name={metricExt.label + (yAxisIsRight ? ' (R)' : '')}
          fill={manipulateColor(0.5, format.stroke, '#fff')}
          data={seriesData}
          dataKey="y"
          isAnimationActive={false}
          yAxisId={yAxisId}
          shape={<CategoryDistributionBar yAxis={yAxisGroup} colors={metricExt.format.colors} />}
          barSize={0}
        />
      );
    }
  });
  return ret;
}
